import Accordion from "@material-ui/core/Accordion";
import AccordionDetails from "@material-ui/core/AccordionDetails";
import AccordionSummary from "@material-ui/core/AccordionSummary";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import { grey } from "@material-ui/core/colors";
import Dialog from "@material-ui/core/Dialog";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import {
  makeStyles,
  StyledComponentProps,
  StyleRulesCallback,
  Theme,
  withStyles,
} from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import ExpandMoreIcon from "@material-ui/icons/ExpandMore";
import Alert from "@material-ui/lab/Alert";
import { isEqual } from "lodash";
import * as React from "react";
import { connect } from "react-redux";
import { useLocation } from "react-router-dom";
import { isFeatureEnabled } from "src/utilities";

import { getCustomerGroupUrl } from "../../api";
import { CustomerGroupListItem } from "../../lib/models/CustomerGroupListItem";
import { Dispatch, StoreState } from "../../store";
import { CustomerGroupFilterState, CustomerType } from "../../types/index";
import CustomerTypeFilter from "../CustomerTypeFilter";
import {
  CustomerContactChannelsFilter,
  CustomerInformationFilter,
  CustomerValueAnalysisFilter,
  NextBestActivityFilter,
} from "../Filters";
import { FilterGroup } from "../Filters/constants";
import HousingSituationsFilter from "../Filters/HousingSituationsFilter";
import {
  ElectricityFilter,
  GasFilter,
  GlassFiberFilter,
  HeatingFilter,
} from "../Filters/ProductTypeFilter";
import ActiveCustomerGroupHint from "./ActiveCustomerGroupHint";
import ChangeFilterSettingForCustomerGroupDialog from "./ChangeFilterSettingForCustomerGroupDialog";
import CustomerGroupView from "./GroupDetailsView";

const useMapSelectionWarningStyles = makeStyles({
  link: {
    borderBottom: "1px dotted rgba(0, 0, 0, 0.3)",
  },
});

function MapSelectionWarning() {
  const location = useLocation();
  const classes = useMapSelectionWarningStyles();
  return (
    <>
      {location.pathname !== "/geoselection" && (
        <Grid item xs={12}>
          <Grid item={true} xs={12}>
            <Alert severity="info">
              Die Filterergebnisse werden durch eine Auswahl auf der Karte
              eingeschränkt. Die Kartenauswahl kann auf der Seite{" "}
              <Link href="/geoselection" className={classes.link}>
                Geoselektion
              </Link>{" "}
              angepasst oder entfernt werden. Die übrigen Filtereinstellungen
              bleiben dabei erhalten.
            </Alert>
          </Grid>
        </Grid>
      )}
    </>
  );
}

function mapStateToProps({
  customerGroupFilter,
  customerGroupResult,
  activeCustomerGroup,
  customerGroupEditOption,
}: StoreState) {
  return {
    customerGroupFilter,
    customerGroupResult,
    activeCustomerGroup,
    customerGroupEditOption,
  };
}

function mapDispatchToProps(dispatch: Dispatch) {
  return {
    changeCustomerGroupFilter: (payload: Partial<CustomerGroupFilterState>) =>
      dispatch.customerGroupFilter.changeCustomerGroupFilter(payload),
    resetCustomerGroupFilter: () =>
      dispatch.customerGroupFilter.resetCustomerGroupFilter(),
    setActiveCustomerGroup: (customerGroup: CustomerGroupListItem | null) =>
      dispatch.activeCustomerGroup.setActiveCustomerGroup(customerGroup),
    setCustomerGroupEditOption: (customerGroup: CustomerGroupListItem | null) =>
      dispatch.customerGroupEditOption.setCustomerGroupEditOption(
        customerGroup
      ),
    editCustomerGroup: (
      customerGroupFilter: CustomerGroupFilterState,
      uuid: string
    ) =>
      dispatch.customerGroupList.editCustomerGroup({
        filterSettings: customerGroupFilter,
        customerGroupUuid: uuid,
      }),
  };
}
type Props = ReturnType<typeof mapStateToProps> &
  ReturnType<typeof mapDispatchToProps> & { expanded?: boolean };

interface State {
  accordionExpanded: boolean;
  isModalOpen: boolean;
  confirmEditCustomerGroupDialogOpen: boolean;
}

class CustomerGroupFilter extends React.Component<
  Props & StyledComponentProps,
  State
> {
  public state = {
    accordionExpanded: this.props.expanded ?? true,
    isModalOpen: false,
    confirmEditCustomerGroupDialogOpen: false,
  };

  private token: string | null;

  private handleEditCustomerGroup() {
    this.setState({ confirmEditCustomerGroupDialogOpen: true });
  }

  private handleNoEditCustomerGroup() {
    this.props.setCustomerGroupEditOption(null);
  }

  public async componentDidMount() {
    this.token = await localStorage.getItem("login");
  }

  public componentDidUpdate() {
    if (
      this.props.activeCustomerGroup &&
      !isEqual(
        this.props.customerGroupFilter,
        this.props.activeCustomerGroup.filterSettings
      )
    ) {
      this.props.setCustomerGroupEditOption(this.props.activeCustomerGroup);
      this.props.setActiveCustomerGroup(null);
    }
  }

  public render() {
    const { classes } = this.props;

    return (
      <>
        <Accordion
          expanded={this.state.accordionExpanded}
          onChange={this.handleAccordionChange}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="h6">Kundengruppenfilter</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Grid container={true} spacing={2}>
              {this.hasPolygonMapSelection() && <MapSelectionWarning />}
              <Grid item xs={12}>
                <Grid container={true} alignItems="flex-end">
                  <Grid item={true} xs={4}>
                    <CustomerTypeFilter
                      onChange={this.handleCustomerTypeChange}
                      customerType={this.props.customerGroupFilter.customerType}
                    />
                  </Grid>
                  <Grid item={true} xs={8}>
                    <ActiveCustomerGroupHint
                      activeCustomerGroup={this.props.activeCustomerGroup}
                      onEditClick={() => this.handleEditCustomerGroup()}
                      onCloseClick={() => this.handleNoEditCustomerGroup()}
                    />
                  </Grid>
                </Grid>
              </Grid>
              <Grid item={true} xs={12} md={6} lg={4}>
                <CustomerValueAnalysisFilter
                  customerValueAnalysisFilter={(({
                    minClv,
                    maxClv,
                    minChurn,
                    maxChurn,
                    minChurnDelta,
                    maxChurnDelta,
                  }) => ({
                    minClv,
                    maxClv,
                    minChurn,
                    maxChurn,
                    minChurnDelta,
                    maxChurnDelta,
                  }))(this.props.customerGroupFilter)}
                  enabled={
                    this.props.customerGroupFilter.filterGroupsEnabled
                      .customerValueAnalysis
                  }
                  changeCustomerGroupFilter={
                    this.props.changeCustomerGroupFilter
                  }
                  handleEnabledChange={this.toggleFilterGroupEnabled}
                />
              </Grid>
              <Grid item={true} xs={12} md={6} lg={4}>
                <NextBestActivityFilter
                  nextBestActivityFilter={
                    this.props.customerGroupFilter.nextBestActivityFilter
                  }
                  enabled={
                    this.props.customerGroupFilter.filterGroupsEnabled
                      .nextBestActivity
                  }
                  changeCustomerGroupFilter={
                    this.props.changeCustomerGroupFilter
                  }
                  handleEnabledChange={this.toggleFilterGroupEnabled}
                />
              </Grid>
              <Grid item={true} xs={12} md={6} lg={4}>
                <CustomerInformationFilter
                  customerInformationFilter={(({
                    ageGroupType,
                    salutation,
                    customerAddressPostalCodes,
                    complaintType,
                    activeChurnedType,
                    customerDuration,
                  }) => ({
                    ageGroupType,
                    salutation,
                    customerAddressPostalCodes,
                    complaintType,
                    activeChurnedType,
                    customerDuration,
                  }))(this.props.customerGroupFilter)}
                  enabled={
                    this.props.customerGroupFilter.filterGroupsEnabled
                      .customerInformation
                  }
                  changeCustomerGroupFilter={
                    this.props.changeCustomerGroupFilter
                  }
                  handleEnabledChange={this.toggleFilterGroupEnabled}
                />
              </Grid>
              <Grid item={true} xs={12} md={6} lg={4}>
                <CustomerContactChannelsFilter
                  customerContactChannelsFilter={(({
                    optIn,
                    phone,
                    email,
                  }) => ({
                    optIn,
                    phone,
                    email,
                  }))(this.props.customerGroupFilter)}
                  enabled={
                    this.props.customerGroupFilter.filterGroupsEnabled
                      .customerContactChannels
                  }
                  changeCustomerGroupFilter={
                    this.props.changeCustomerGroupFilter
                  }
                  handleEnabledChange={this.toggleFilterGroupEnabled}
                />
              </Grid>
              {isFeatureEnabled(
                process.env.REACT_APP_FEATURE_HOUSING_SITUATION
              ) ? (
                <Grid item={true} xs={12} md={6} lg={4}>
                  <HousingSituationsFilter
                    housingSituationsFilter={
                      this.props.customerGroupFilter.housingSituationsFilter
                    }
                    enabled={
                      this.props.customerGroupFilter.filterGroupsEnabled
                        .housingSituations
                    }
                    changeCustomerGroupFilter={
                      this.props.changeCustomerGroupFilter
                    }
                    handleEnabledChange={this.toggleFilterGroupEnabled}
                  />
                </Grid>
              ) : (
                <></>
              )}
              <Grid item={true} xs={12} md={6} lg={4}>
                <ElectricityFilter
                  electricityFilter={
                    this.props.customerGroupFilter.electricityFilter
                  }
                  superordinateFilter="CustomerGroupFilter"
                  enabled={
                    this.props.customerGroupFilter.filterGroupsEnabled
                      .electricity
                  }
                  changeFilter={this.props.changeCustomerGroupFilter}
                  handleEnabledChange={this.toggleFilterGroupEnabled}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <GasFilter
                  gasFilter={this.props.customerGroupFilter.gasFilter}
                  superordinateFilter="CustomerGroupFilter"
                  enabled={
                    this.props.customerGroupFilter.filterGroupsEnabled.gas
                  }
                  changeFilter={this.props.changeCustomerGroupFilter}
                  handleEnabledChange={this.toggleFilterGroupEnabled}
                />
              </Grid>
              <Grid item xs={12} md={6} lg={4}>
                <HeatingFilter
                  heatingFilter={this.props.customerGroupFilter.heatingFilter}
                  superordinateFilter="CustomerGroupFilter"
                  enabled={
                    this.props.customerGroupFilter.filterGroupsEnabled.heating
                  }
                  changeFilter={this.props.changeCustomerGroupFilter}
                  handleEnabledChange={this.toggleFilterGroupEnabled}
                />
              </Grid>
              {isFeatureEnabled(process.env.REACT_APP_FEATURE_GLASS_FIBER) ? (
                <Grid item xs={12} md={6} lg={4}>
                  <GlassFiberFilter
                    glassFiberFilter={
                      this.props.customerGroupFilter.glassFiberFilter
                    }
                    superordinateFilter="CustomerGroupFilter"
                    enabled={
                      this.props.customerGroupFilter.filterGroupsEnabled
                        .glassFiber
                    }
                    changeFilter={this.props.changeCustomerGroupFilter}
                    handleEnabledChange={this.toggleFilterGroupEnabled}
                  />
                </Grid>
              ) : (
                <></>
              )}
              <Grid item={true} xs={12}>
                <Grid container={true} justify="flex-end">
                  <Grid item={true}>
                    <div className={classes && classes.wrapper}>
                      <Button
                        className={classes && classes.button}
                        onClick={this.handleResetPress}
                        variant="contained"
                        disabled={this.props.customerGroupResult.loading}
                      >
                        Filter zurücksetzen
                      </Button>
                      {this.props.customerGroupResult.loading && (
                        <CircularProgress
                          size={24}
                          className={classes && classes.buttonProgress}
                        />
                      )}
                    </div>
                  </Grid>
                  <Grid item={true}>
                    <div className={classes && classes.wrapper}>
                      <Button
                        className={classes && classes.button}
                        color="secondary"
                        variant="contained"
                        onClick={() => this.setState({ isModalOpen: true })}
                        disabled={
                          !this.isValidCustomerGroup() ||
                          this.props.customerGroupResult.loading
                        }
                      >
                        Ergebnistabelle anzeigen
                      </Button>
                      {this.props.customerGroupResult.loading && (
                        <CircularProgress
                          size={24}
                          className={classes && classes.buttonProgress}
                        />
                      )}
                    </div>
                  </Grid>
                </Grid>
              </Grid>
            </Grid>
            <Dialog
              open={this.state.isModalOpen}
              onClose={() => this.setState({ isModalOpen: false })}
              fullWidth={true}
              maxWidth="lg"
              scroll="body"
            >
              <CustomerGroupView
                getDownloadHref={(
                  tableFilter?: { [field: string]: string },
                  sortBy?: string,
                  sortDirection?: string
                ) =>
                  getCustomerGroupUrl(
                    this.props.customerGroupFilter,
                    true,
                    tableFilter,
                    sortBy,
                    sortDirection
                  ) + `&token=${this.token}`
                }
              />
            </Dialog>
          </AccordionDetails>
        </Accordion>
        {this.props.customerGroupEditOption && (
          <ChangeFilterSettingForCustomerGroupDialog
            open={this.state.confirmEditCustomerGroupDialogOpen}
            customerGroup={
              (this.props
                .customerGroupEditOption as unknown) as CustomerGroupListItem
            }
            onClose={() =>
              this.setState({ confirmEditCustomerGroupDialogOpen: false })
            }
            onConfirm={() => {
              this.props.setCustomerGroupEditOption(null);
              this.setState({
                confirmEditCustomerGroupDialogOpen: false,
              });
            }}
            onAbort={() => {
              this.props.setCustomerGroupEditOption(null);
              this.setState({
                confirmEditCustomerGroupDialogOpen: false,
              });
            }}
          />
        )}
      </>
    );
  }

  private hasPolygonMapSelection() {
    return this.props.customerGroupFilter.polygonMapSelection.length > 0;
  }

  private handleResetPress = () => {
    this.props.resetCustomerGroupFilter();
  };

  private handleAccordionChange = (
    event: React.ChangeEvent<{}>,
    expanded: boolean
  ): void => {
    this.setState({ accordionExpanded: expanded });
  };

  private toggleFilterGroupEnabled = (
    filterGroup: FilterGroup,
    enabled: boolean
  ) => {
    this.props.changeCustomerGroupFilter({
      filterGroupsEnabled: {
        ...this.props.customerGroupFilter.filterGroupsEnabled,
        [filterGroup]: enabled,
      },
    });
  };

  private isValidCustomerGroup = (): boolean => {
    return this.props.customerGroupResult.meta.count > 0;
  };

  private handleCustomerTypeChange = (customerType: CustomerType[]) => {
    this.props.changeCustomerGroupFilter({
      customerType,
    });
  };
}

const styles: StyleRulesCallback<Theme, Props> = (theme: Theme) => ({
  accordion: {
    boxShadow: "none !important",
    border: `1px solid ${grey[400]}`,
    borderRadius: "4px",
  },
  alert: {
    "& .MuiAlert-icon": {
      alignItems: "center",
    },
  },
  button: {
    margin: theme.spacing(1),
  },
  buttonProgress: {
    left: "50%",
    marginLeft: -12,
    marginTop: -12,
    position: "absolute",
    top: "50%",
  },
  divWidth: { width: "100%" },
  paper: {
    padding: theme.spacing(2),
  },
  saveButton: {
    margin: theme.spacing(1),
    marginRight: theme.spacing(4),
  },
  spacingDivider: {
    margin: theme.spacing(2, 0),
  },
  wrapper: {
    position: "relative",
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withStyles(styles)(CustomerGroupFilter));



// WEBPACK FOOTER //
// ./src/components/CustomerGroups/CustomerGroupFilter.tsx